/**
 * LiveTurnCardsOverlay — the N cards inside the ONE overlay lane (§11 WS-2 / §12
 * PR-F2 of docs/PLAN-mid-turn-fold-in-20260915.md; T-R13, Miles 2026-09-20, and his
 * 2026-09-21 directive on the card itself).
 *
 * The pane has exactly one pending-overlay lane (turnIndicator.ts — five incidents
 * behind that rule). "Several spinning widgets" are therefore N CARDS rendered as a
 * React SIBLING inside that lane, the SpendChip's precedent: the own pill, its
 * html-string contract (shape detection, the stop-button id), its extraction and
 * the byte-pinned `renderMessages` return literal are untouched.
 *
 * A card is composed HERE rather than handed over as one html blob, because its
 * steps rail is interactive (a disclosure that opens the whole run) and its rows
 * are keyed so they rotate instead of flickering. The head and the lanes are still
 * html strings from liveTurnCardHtml.ts — escaped at the producer, nothing from a
 * message body or a tool reaches them.
 *
 * Own clock: the cards carry elapsed times, and re-rendering the whole transcript
 * every second to move them would be the wrong price — so this component ticks
 * itself (the ThreadTurnIndicator idiom) and only while it has something to show.
 *
 * What it never does: light a processing flag, take a streaming lease
 * (streamingFlag.ts), dispatch anything, or draw a stop control on another
 * person's work. A card is what someone ELSE is doing.
 */
import React, { useEffect, useState } from 'react';
import type { LiveTurnCard } from '../../services/groupThreads/liveTurns';
import {
  GROUP_LIVE_TURN_SOURCE_TAG,
  buildLiveTurnCardHeadHtml,
  buildLiveTurnLanesHtml,
  buildLiveTurnOwnHeaderHtml,
  type LiveTurnCardHeader,
} from '../../services/globalTools/liveTurnCardHtml';
import { LiveTurnStepsRail } from './LiveTurnStepsRail';

export const LIVE_TURN_CARD_TICK_MS = 1_000;

function useClockTick(active: boolean): number {
  const [now, setNow] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), LIVE_TURN_CARD_TICK_MS);
    return () => clearInterval(timer);
  }, [active]);
  return now;
}

export interface LiveTurnCardsOverlayProps {
  /** The cards to draw (never the viewer's own tab's turn — composeLiveTurnCards). */
  cards: ReadonlyArray<LiveTurnCard>;
  /** How many more there are past the cap. */
  hidden: number;
  /** The host resolves a NAME and a glyph — never a role (R8). */
  headerFor: (card: LiveTurnCard) => LiveTurnCardHeader;
  /**
   * The viewer's own running turn, when its pill is on screen: its steps rail hangs
   * under that pill (the pill itself keeps the stage, the clock, the lanes and Stop
   * — the lanes are never drawn twice for one turn).
   */
  ownCard?: LiveTurnCard | null;
}

/** ONE card: the head html, the lanes when the primary parallelised, the steps rail. */
function LiveTurnCardView({ card, header, nowMs }: { card: LiveTurnCard; header: LiveTurnCardHeader; nowMs: number }): JSX.Element {
  const lanes = buildLiveTurnLanesHtml(card, nowMs);
  return (
    <div
      className={`pending-live-turn-card is-${card.state === 'converging' ? 'converging' : 'running'}`}
      data-source-tag={GROUP_LIVE_TURN_SOURCE_TAG}
      data-live-turn-task={card.taskId}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      {/* The head is composed from escaped strings by liveTurnCardHtml.ts. */}
      <div dangerouslySetInnerHTML={{ __html: buildLiveTurnCardHeadHtml(card, header, nowMs) }} />
      {lanes ? <div dangerouslySetInnerHTML={{ __html: lanes }} /> : null}
      <LiveTurnStepsRail card={card} />
    </div>
  );
}

export function LiveTurnCardsOverlay({ cards, hidden, headerFor, ownCard }: LiveTurnCardsOverlayProps): JSX.Element | null {
  const active = cards.length > 0 || Boolean(ownCard);
  const now = useClockTick(active);
  if (!active) return null;
  return (
    <div className="pending-live-turns" data-live-turn-cards={String(cards.length)}>
      {/* The own turn's rail hangs under the own pill — no second head, no second
          lane rail: that pill already carries the name, the stage, the branches
          and Stop. */}
      {ownCard ? <LiveTurnStepsRail card={ownCard} /> : null}
      {cards.map((c) => (
        <LiveTurnCardView key={c.taskId} card={c} header={headerFor(c)} nowMs={now} />
      ))}
      {hidden > 0 ? (
        <div className="pending-live-turn-more">{`+${Math.floor(hidden)} more`}</div>
      ) : null}
    </div>
  );
}

/** The name + glyph drawn ABOVE the viewer's own pill in a shared room. */
export function LiveTurnOwnHeader({ header }: { header: LiveTurnCardHeader }): JSX.Element {
  return (
    <div
      className="pending-live-turn-own-header"
      dangerouslySetInnerHTML={{ __html: buildLiveTurnOwnHeaderHtml(header) }}
    />
  );
}
